/* eslint-disable @typescript-eslint/no-explicit-any */
import { getJwt, type Id, HttpError } from "./common";

const BASE_URL = import.meta.env.VITE_API_URL;

export function createApiResource<TModel, TId extends Id = string>(path: string) {
    const URL = `${BASE_URL}/${path}`;

    return {
        list: async (locationId: string): Promise<TModel[]> => {
            const response = await fetch(URL, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${getJwt()}`,
                    'x-location-id': locationId
                }
            });

            const contentType = response.headers.get('content-type') ?? '';
            const data = contentType.includes('json') ? await response.json().catch(() => null) : await response.text().catch(() => '');

            if (!response.ok) throw new HttpError(response.status, data);
            return await data;
        },
        get: async (locationId: string, id: TId): Promise<TModel> => {
            const response = await fetch(`${URL}/${id}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${getJwt()}`,
                    'x-location-id': locationId
                }
            });
            const data = await response.json();
            if (!response.ok) throw new HttpError(response.status, data);
            return await data;
        },
        save: async (locationId: string, item: TModel): Promise<TModel | void> => {
            // If the item has an id, we assume it's an update
            if ((item as any).id) {
                const response = await fetch(`${URL}/${(item as any).id}`, {
                    method: 'PUT',
                    body: JSON.stringify(item),
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${getJwt()}`,
                        'x-location-id': locationId
                    },
                });

                const contentType = response.headers.get('content-type') ?? '';
                const data = contentType.includes('json') ? await response.json().catch(() => null) : await response.text().catch(() => '');

                if (!response.ok) throw new HttpError(response.status, data);
            }
            else {
                const response = await fetch(URL, {
                    method: 'POST',
                    body: JSON.stringify(item),
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${getJwt()}`,
                        'x-location-id': locationId
                    },
                });

                const contentType = response.headers.get('content-type') ?? '';
                const data = contentType.includes('json') ? await response.json().catch(() => null) : await response.text().catch(() => '');

                if (!response.ok) throw new HttpError(response.status, data);
                return await data;
            }
        },
        create: async (locationId: string, item: TModel): Promise<TModel> => {
            const response = await fetch(URL, {
                method: 'POST',
                body: JSON.stringify(item),
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getJwt()}`,
                    'x-location-id': locationId
                },
            });

            const contentType = response.headers.get('content-type') ?? '';
            const data = contentType.includes('json') ? await response.json().catch(() => null) : await response.text().catch(() => '');

            if (!response.ok) throw new HttpError(response.status, data);
            return await data;
        },
        update: async (locationId: string, item: TModel): Promise<void> => {
            const response = await fetch(`${URL}/${(item as any).id}`, {
                method: 'PUT',
                body: JSON.stringify(item),
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getJwt()}`,
                    'x-location-id': locationId
                },
            });

            const contentType = response.headers.get('content-type') ?? '';
            const data = contentType.includes('json') ? await response.json().catch(() => null) : await response.text().catch(() => '');

            if (!response.ok) throw new HttpError(response.status, data);
        },
        delete: async (locationId: string, id: TId): Promise<void> => {
            const response = await fetch(`${URL}/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${getJwt()}`,
                    'x-location-id': locationId
                }
            });

            const contentType = response.headers.get('content-type') ?? '';
            const data = contentType.includes('json') ? await response.json().catch(() => null) : await response.text().catch(() => '');

            if (!response.ok) throw new HttpError(response.status, data);
        }
    }
}
